import React from 'react';
import BlockCustomFeaturesSection from './BlockCustomFeaturesSection';
import {
  FLAT_PATTERNS,
  flatPatternLabel,
  isVillaStreet,
  resolveBlockCode,
  villaPatternLabel,
} from './communitySiteUtils';

const NumberStepper = ({ label, value, min = 0, max = 99, onChange }) => (
  <div className="community-advanced-stepper">
    <span className="community-advanced-label">{label}</span>
    <div className="community-stepper-controls">
      <button type="button" disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}>
        −
      </button>
      <span>{value}</span>
      <button type="button" disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}>
        +
      </button>
    </div>
  </div>
);

const BlockAdvancedSection = ({ block, onChange }) => {
  const villa = isVillaStreet(block);
  const blockCode = resolveBlockCode(block.name, block.blockPrefix);
  const patternLabel = villa
    ? p => villaPatternLabel(p, blockCode)
    : p => flatPatternLabel(p, blockCode);

  const update = (field, value) => {
    onChange({ ...block, [field]: value });
  };

  return (
    <div className="community-block-advanced">
      <h4 className="community-block-advanced-title">Advanced</h4>
      <p className="community-block-advanced-subtitle">Optional</p>

      {!villa && (
        <NumberStepper
          label="Basement Floors"
          value={block.basementFloors ?? 0}
          max={5}
          onChange={v => update('basementFloors', v)}
        />
      )}

      <label className="community-field community-field--compact">
        <span>{villa ? 'Street Prefix' : 'Block Prefix'}</span>
        <input
          type="text"
          placeholder={villa ? 'e.g. A, North' : 'e.g. A, B1, North'}
          value={block.blockPrefix ?? ''}
          onChange={e => update('blockPrefix', e.target.value)}
        />
      </label>

      <label className="community-field community-field--compact">
        <span>{villa ? 'Starting Villa Number' : 'Starting Flat Number'}</span>
        <input
          type="number"
          min={1}
          value={block.startingFlatNumber ?? 101}
          onChange={e => {
            const n = parseInt(e.target.value, 10);
            if (!Number.isNaN(n)) update('startingFlatNumber', n);
          }}
        />
      </label>

      <label className="community-field community-field--compact">
        <span>{villa ? 'Villa Number Pattern' : 'Flat Number Pattern'}</span>
        <select
          value={block.flatPattern ?? FLAT_PATTERNS.BLOCK_DASH_UNIT}
          onChange={e => update('flatPattern', e.target.value)}
        >
          {Object.values(FLAT_PATTERNS).map(p => (
            <option key={p} value={p}>
              {patternLabel(p)}
            </option>
          ))}
        </select>
      </label>

      {!villa && (
        <>
          <NumberStepper
            label="Lift Count"
            value={block.liftCount ?? 1}
            max={20}
            min={0}
            onChange={v => update('liftCount', v)}
          />
          <NumberStepper
            label="Staircase Count"
            value={block.staircaseCount ?? 1}
            max={10}
            min={0}
            onChange={v => update('staircaseCount', v)}
          />
          <NumberStepper
            label="Corridor Count"
            value={block.corridorCount ?? 1}
            max={20}
            min={0}
            onChange={v => update('corridorCount', v)}
          />
          <NumberStepper
            label="Parking Floors"
            value={block.parkingFloors ?? 0}
            max={10}
            onChange={v => update('parkingFloors', v)}
          />
          <label className="community-advanced-checkbox">
            <input
              type="checkbox"
              checked={Boolean(block.terraceAccess)}
              onChange={e => update('terraceAccess', e.target.checked)}
            />
            <span>Terrace Access</span>
          </label>
          <p className="community-advanced-hint">
            Can maintenance teams access the terrace/roof area of this block?
          </p>
        </>
      )}

      <hr className="community-advanced-divider" />
      <BlockCustomFeaturesSection
        features={block.customFeatures}
        onChange={features => update('customFeatures', features)}
      />
    </div>
  );
};

export default BlockAdvancedSection;
